import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { heartBreak } from '../styles/GlobalStyle';

const Wrap = styled.span<{ $size: number }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $size }) => Math.round($size * 0.16)}px;
  font-size: ${({ $size }) => $size}px;
  line-height: 1;
`;

const Pip = styled.span<{ $filled: boolean; $breaking: boolean }>`
  display: inline-block;
  transition: filter 200ms ease, opacity 200ms ease;

  ${({ $filled }) =>
    $filled
      ? css`
          filter: drop-shadow(0 2px 3px rgba(255, 123, 156, 0.5));
        `
      : css`
          /* Spent hearts stay in place as empty sockets so the row never reflows. */
          filter: grayscale(1) opacity(0.32);
        `}

  ${({ $breaking }) =>
    $breaking &&
    css`
      animation: ${heartBreak} 900ms ease-out both;
    `}
`;

interface HeartsProps {
  hearts: number;
  /**
   * Total sockets to draw. Pass the room's `settings.startingHearts` so spent
   * hearts leave an empty socket behind and the row never reflows mid-game.
   * Defaults to the hearts held, which is right in the lobby.
   */
  max?: number;
  size?: number;
  /** Plays the shatter animation on the heart that was just lost. */
  breaking?: boolean;
}

export function Hearts({ hearts, max, size = 20, breaking = false }: HeartsProps) {
  const { t } = useTranslation();
  const total = Math.max(max ?? hearts, hearts);

  return (
    <Wrap
      $size={size}
      role="img"
      aria-label={hearts > 0 ? t('hearts.remaining', { count: hearts }) : t('hearts.out')}
    >
      {Array.from({ length: total }, (_, index) => {
        const filled = index < hearts;
        // Only the first spent socket shatters — the rest were lost in earlier rounds.
        const isFreshlyLost = breaking && index === hearts;
        return (
          <Pip key={index} $filled={filled} $breaking={isFreshlyLost} aria-hidden="true">
            {filled ? '💗' : '🤍'}
          </Pip>
        );
      })}
    </Wrap>
  );
}
